import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import {
  VALID_SOURCES,
  type CaseRecord,
  type Direction,
  type FileKind,
  type SourceType,
} from "../types";

type Mode = "file" | "audio";

interface Props {
  caseId: string;
  mode: Mode;
  onRecordCreated: (record: CaseRecord) => void;
  onDone: () => void;
}

// File-picker-driven upload for everything except screenshots.
// For mode="file" we accept PDFs, Office docs, and images.
// For mode="audio" we accept common audio containers (goes through Whisper
// server-side). Users can pick multiple files at once; each becomes its own
// record, uploaded by a bounded pool (MAX_CONCURRENT at a time) so one slow
// or stalled file never blocks the others, while we still don't fan out an
// unbounded number of concurrent Worker requests.
const MAX_CONCURRENT = 3;
const CONFIG: Record<
  Mode,
  {
    title: string;
    accept: string;
    hint: string;
    fallbackSource: SourceType;
    defaultDirection: Direction;
  }
> = {
  file: {
    title: "Upload files",
    accept:
      "image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.pdf,.docx,.xlsx",
    hint: "PDF, Word (.docx), Excel (.xlsx), or an image (including iPhone HEIC). Pick several at once — each becomes its own record and is extracted in turn.",
    fallbackSource: "other",
    defaultDirection: "inbound",
  },
  audio: {
    title: "Upload audio files",
    accept: "audio/*,.mp3,.m4a,.wav,.webm,.ogg,.flac",
    hint: "MP3, M4A, WAV, WebM, OGG, or FLAC. Transcribed automatically via Whisper. Multi-select supported.",
    fallbackSource: "phone_call",
    defaultDirection: "inbound",
  },
};

function coerceSource(s: string | null, fallback: SourceType): SourceType {
  if (s && (VALID_SOURCES as readonly string[]).includes(s))
    return s as SourceType;
  return fallback;
}

function coerceDirection(
  s: string | null,
  fallback: Direction,
): Direction {
  if (s === "inbound" || s === "outbound") return s;
  return fallback;
}

function fileEmoji(kind: FileKind): string {
  switch (kind) {
    case "pdf":
      return "📄";
    case "docx":
      return "📝";
    case "xlsx":
      return "📊";
    case "image":
      return "🖼️";
    case "audio":
      return "🎙";
    case "video":
      return "🎞";
    default:
      return "📎";
  }
}

// Prefer the server-returned correspondence date (extracted
// deterministically from the file's body/OCR text) over today's date.
// Falls back to "now" only when the extractor found nothing — which
// signals the file really has no date at all, not that we should
// guess at one.
//
// Noon UTC keeps the calendar day stable when the ISO timestamp is
// rendered via `.toLocaleDateString()` in a western timezone —
// midnight UTC would back-date the display by a day for anyone in
// the US, which was the bug that prompted this treatment.
function resolveRecordDate(iso: string | null): string {
  if (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
    return `${iso.slice(0, 10)}T12:00:00.000Z`;
  }
  return new Date().toISOString();
}

// iPhone photos arrive as HEIC/HEIF, which the Anthropic Vision API
// doesn't accept (only jpeg/png/gif/webp). Convert to JPEG in the
// browser by decoding through <img> and re-encoding via canvas —
// Safari decodes HEIC natively and Chrome 118+ does too. Browsers
// that can't decode HEIC hit the onerror path and the user sees a
// clear message instead of a silent failure.
function isHeicFile(file: File): boolean {
  const m = (file.type || "").toLowerCase();
  if (m === "image/heic" || m === "image/heif") return true;
  // Some browsers (Chrome, Firefox) don't set a mime on .heic files —
  // fall back to the extension.
  const name = file.name.toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
}

// Try the browser's native image decoder first — Safari can decode
// HEIC natively, and going through <img>+canvas avoids shipping the
// ~1.3MB heic2any chunk for those users. Chrome/Firefox will hit the
// onerror path and fall back to heic2any, which uses a WASM HEIF
// decoder (and new Function internally, hence the CSP carve-out).
async function convertHeicViaCanvas(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Canvas not supported"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            reject(new Error("Conversion failed"));
            return;
          }
          const name = file.name.replace(/\.(heic|heif)$/i, ".jpg");
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        0.92,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Browser cannot decode HEIC"));
    };
    img.src = url;
  });
}

async function convertHeicViaLib(file: File): Promise<File> {
  const { default: heic2any } = await import("heic2any");
  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });
  // heic2any returns Blob | Blob[] — multi-frame HEICs split into
  // frames. Keep the first; correspondence HEICs are almost always
  // single-frame photos.
  const blob = Array.isArray(converted) ? converted[0]! : converted;
  const baseName = file.name.replace(/\.(heic|heif)$/i, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    return await convertHeicViaCanvas(file);
  } catch {
    return await convertHeicViaLib(file);
  }
}

type ItemStatus =
  | "queued"
  | "uploading"
  | "extracting"
  | "saving"
  | "done"
  | "error";

interface UploadItem {
  id: string;
  name: string;
  status: ItemStatus;
  error?: string;
}

export default function FileUploadPanel({
  caseId,
  mode,
  onRecordCreated,
  onDone,
}: Props) {
  const cfg = CONFIG[mode];
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Pending work queue + loop guard. Refs rather than state because
  // handleFiles may be called while runProcessor is mid-loop and we
  // need the new entries visible on the next iteration's check —
  // setState updates wouldn't be readable synchronously from inside
  // the running async loop.
  const queueRef = useRef<Array<{ file: File; id: string }>>([]);
  const processingRef = useRef(false);
  const hadErrorRef = useRef(false);
  const doneTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (doneTimerRef.current !== null) {
        window.clearTimeout(doneTimerRef.current);
      }
    };
  }, []);

  function updateItem(id: string, patch: Partial<UploadItem>) {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  }

  async function handleOneFile(inputFile: File, id: string): Promise<boolean> {
    updateItem(id, { status: "uploading" });
    try {
      let file = inputFile;
      if (mode === "file" && isHeicFile(inputFile)) {
        try {
          file = await convertHeicToJpeg(inputFile);
        } catch (err) {
          updateItem(id, {
            status: "error",
            error: `HEIC conversion failed: ${(err as Error).message}`,
          });
          return false;
        }
      }
      const upload = await api.uploadFile(caseId, file, (percent) => {
        // Once the file bytes are all on the wire, the Worker is
        // running extraction (OCR / PDF text). Flip the label so the
        // user sees the phase they're actually waiting on.
        if (percent >= 100) updateItem(id, { status: "extracting" });
      });
      updateItem(id, { status: "saving" });

      const extracted = upload.extracted;
      const recordDate = resolveRecordDate(extracted.record_date);
      const bodyText = extracted.body_text ?? extracted.ocr_text ?? null;
      const subject =
        extracted.subject?.trim() || file.name || "Uploaded file";

      const { record } = await api.createRecord(caseId, {
        record_date: recordDate,
        direction: coerceDirection(extracted.direction, cfg.defaultDirection),
        source_type: coerceSource(extracted.source_type, cfg.fallbackSource),
        from_addr: extracted.from_addr,
        to_addr: extracted.to_addr,
        subject,
        body_text: bodyText,
        ocr_text: extracted.ocr_text,
        file_r2_key: upload.file_r2_key,
        file_name: upload.file_name,
        file_mime_type: upload.file_mime_type,
        extraction_confidence: extracted.extraction_confidence,
        date_source: extracted.date_source,
      });
      // True content duplicate is dropped server-side (no record returned);
      // the upload still counts as done — the content is already in the case.
      if (record) onRecordCreated(record);
      updateItem(id, { status: "done" });
      return true;
    } catch (err) {
      updateItem(id, { status: "error", error: (err as Error).message });
      return false;
    }
  }

  // Drain the pending queue with a bounded pool of concurrent workers.
  // Each file is fully independent (own request, own row state, own
  // error), so a slow or stalled file only ties up its own worker slot —
  // it can NEVER freeze the others (the bug: sequential draining left
  // every later file stuck on "Waiting" behind a single hung upload).
  // Cap at MAX_CONCURRENT so we don't hammer the Worker with an unbounded
  // fan-out. Idempotent — a second runProcessor() short-circuits on
  // processingRef, and any files pushed mid-run are picked up by a free
  // worker (or the re-drain do/while below if the pool just emptied).
  async function runProcessor() {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      async function worker() {
        while (queueRef.current.length > 0) {
          const next = queueRef.current.shift()!;
          const ok = await handleOneFile(next.file, next.id);
          if (!ok) hadErrorRef.current = true;
        }
      }
      do {
        const n = Math.min(MAX_CONCURRENT, queueRef.current.length);
        await Promise.all(Array.from({ length: n }, () => worker()));
      } while (queueRef.current.length > 0);
    } finally {
      processingRef.current = false;
    }

    // Queue drained. Auto-close only if no file in this session has
    // errored — otherwise the user needs to see what failed.
    if (!hadErrorRef.current) {
      if (doneTimerRef.current !== null) {
        window.clearTimeout(doneTimerRef.current);
      }
      doneTimerRef.current = window.setTimeout(() => {
        doneTimerRef.current = null;
        onDone();
      }, 900);
    }
  }

  function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    // User added more work — cancel any pending auto-close so the
    // panel doesn't disappear mid-flight if earlier uploads just
    // finished.
    if (doneTimerRef.current !== null) {
      window.clearTimeout(doneTimerRef.current);
      doneTimerRef.current = null;
    }

    // Seed the queue so every file shows up in the progress list
    // before the processor reaches it.
    const seeds: UploadItem[] = files.map((f) => ({
      id: crypto.randomUUID(),
      name: f.name,
      status: "queued",
    }));
    setItems((prev) => [...prev, ...seeds]);
    for (let i = 0; i < files.length; i++) {
      queueRef.current.push({ file: files[i]!, id: seeds[i]!.id });
    }
    void runProcessor();
  }

  const iconKind: FileKind = mode === "audio" ? "audio" : "image";

  return (
    <div className="file-upload-panel">
      <div
        className={`dropzone${dragging ? " dragging" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
          }
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={cfg.accept}
          style={{ display: "none" }}
          onChange={(e) => {
            // Snapshot the FileList into a plain array BEFORE clearing
            // the input value. Setting value="" invalidates the live
            // FileList in most browsers, so reading length/items after
            // the reset silently yields zero.
            const picked = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = "";
            if (picked.length > 0) handleFiles(picked);
          }}
        />
        <div className="dropzone-label">
          Drop files here, or click to choose
        </div>
        <div className="dropzone-hint">{cfg.hint}</div>
      </div>

      {items.length > 0 && (
        <ul className="file-upload-queue">
          {items.map((it) => (
            <li key={it.id} className="file-upload-status">
              <span className="file-upload-icon" aria-hidden>
                {fileEmoji(iconKind)}
              </span>
              <div className="file-upload-meta">
                <div className="file-upload-name">{it.name}</div>
                <div className="file-upload-state">
                  {it.status === "queued" && "Waiting…"}
                  {it.status === "uploading" && (
                    <>
                      <span className="spinner" /> Uploading…
                    </>
                  )}
                  {it.status === "extracting" && (
                    <>
                      <span className="spinner" /> Extracting text…
                    </>
                  )}
                  {it.status === "saving" && (
                    <>
                      <span className="spinner" /> Saving record…
                    </>
                  )}
                  {it.status === "done" && "✓ Added to records"}
                  {it.status === "error" && (
                    <span className="form-error">
                      ⚠ {it.error ?? "Failed"}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
